import { useCallback } from "react";
import { type RegisteredRouter, useNavigate, useSearch } from "@tanstack/react-router";
import type { AnyRoute, FullSearchSchema } from "@tanstack/router-core";

export type FullSearchParams = FullSearchSchema<RegisteredRouter["routeTree"] & AnyRoute>;
export type SearchKeys = keyof FullSearchParams;

/**
 * Allows reading/updating a single search param easily
 * @returns
 */
export function useSearchParam<K extends SearchKeys>(searchParam: K, replaceHistory = true) {
  const searchValue = useSearch({
    strict: false,
    select: (search: FullSearchParams) => search[searchParam as SearchKeys],
  }) as FullSearchParams[K];

  const navigate = useNavigate();

  const setSearchParam = useCallback(
    (newValue: FullSearchParams[K]) => {
      void navigate({
        to: ".",
        search: (prev) => ({
          ...prev,
          [searchParam]: newValue,
        }),
        replace: replaceHistory,
      });
    },
    [navigate, searchParam, replaceHistory],
  );

  return [searchValue, setSearchParam] as const;
}
